import { useEffect, useCallback, useRef } from 'react';
import type { ContextMenuItem } from '@/components/shared/ContextMenu';

interface MobileActionSheetProps {
  /** Accepts ContextMenuItem so it can be fed by the same build*Actions()
      helpers that feed the desktop context menu. Separator items render as
      hairline dividers. */
  items: ContextMenuItem[];
  onClose: () => void;
}

export function MobileActionSheet({ items, onClose }: MobileActionSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const animatingRef = useRef(false);

  // Animate in on mount
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    animatingRef.current = true;
    // Force reflow before adding transition
    sheet.getBoundingClientRect();
    sheet.style.transform = 'translateY(0)';
    const timer = setTimeout(() => {
      animatingRef.current = false;
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) {
      onClose();
      return;
    }
    sheet.style.transform = 'translateY(100%)';
    setTimeout(onClose, 200);
  }, [onClose]);

  const handleItemClick = useCallback(
    (item: ContextMenuItem) => {
      if (item.disabled || !item.onClick) return;
      item.onClick();
      handleClose();
    },
    [handleClose],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="relative w-full rounded-t-xl"
        style={{
          backgroundColor: 'var(--dbgo-panel)',
          maxHeight: '70vh',
          overflowY: 'auto',
          transform: 'translateY(100%)',
          transition: 'transform 300ms cubic-bezier(0.22, 0.68, 0, 1)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div
            className="rounded-full"
            style={{
              width: 40,
              height: 4,
              backgroundColor: 'var(--border-strong)',
            }}
          />
        </div>

        {/* Items */}
        <div className="pb-2">
          {items.map((item, i) => {
            if (item.separator) {
              return (
                <div
                  key={`sep-${i}`}
                  className="my-1 mx-5 h-px"
                  style={{ backgroundColor: 'var(--border-default)' }}
                />
              );
            }
            const Icon = item.icon;
            return (
              <button
                key={i}
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={`w-full flex items-center gap-3 px-5 text-left text-sm ${
                  item.disabled
                    ? 'opacity-40 cursor-default'
                    : item.danger
                      ? 'text-accent-red active:bg-dbgo-tab-hover'
                      : 'text-text-primary active:bg-dbgo-tab-hover'
                }`}
                style={{ height: 48 }}
              >
                {Icon && <Icon size={20} className="flex-shrink-0" />}
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Cancel button */}
        <div className="border-t px-4 py-2" style={{ borderColor: 'var(--border-default)' }}>
          <button
            onClick={handleClose}
            className="w-full text-center text-sm font-medium text-accent-blue active:bg-dbgo-tab-hover rounded-md"
            style={{ height: 48 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
